import React, { useEffect, useRef, useState } from 'react';
import { File, Play as PlayIcon, Table2 } from 'lucide-react';
import { supabase } from '../lib/supabase';
import * as pdfjsLib from 'pdfjs-dist';
import * as XLSX from 'xlsx';

// Official Vite-compatible worker setup (pdfjs-dist v4+)
pdfjsLib.GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/build/pdf.worker.min.mjs', import.meta.url).toString();

export interface ThumbFile {
  id: string;
  name: string;
  size_bytes: number;
  mime_type: string | null;
  storage_path: string;
  created_at: string;
}

const IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml', 'image/avif', 'image/bmp', 'image/tiff'];
const VIDEO_TYPES = ['video/mp4', 'video/webm', 'video/quicktime', 'video/ogg', 'video/x-m4v'];
const AUDIO_TYPES = ['audio/mpeg', 'audio/wav', 'audio/ogg', 'audio/mp4', 'audio/aac', 'audio/x-wav'];
const TEXT_TYPES = ['text/plain', 'text/csv', 'text/html', 'text/css', 'text/javascript', 'application/json', 'application/xml', 'text/xml', 'text/markdown'];
const SHEET_TYPES = ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.ms-excel', 'text/csv'];

const getExt = (name: string): string => name.split('.').pop()?.toLowerCase() || '';

export type ThumbType = 'image' | 'video' | 'audio' | 'pdf' | 'sheet' | 'text' | 'none';

export const getThumbType = (file: ThumbFile): ThumbType => {
  const mime = file.mime_type || '';
  const ext = getExt(file.name);
  if (IMAGE_TYPES.includes(mime) || ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'avif', 'bmp'].includes(ext)) return 'image';
  if (VIDEO_TYPES.includes(mime) || ['mp4', 'webm', 'mov', 'm4v', 'ogv'].includes(ext)) return 'video';
  if (AUDIO_TYPES.includes(mime) || ['mp3', 'wav', 'ogg', 'aac', 'm4a'].includes(ext)) return 'audio';
  if (mime === 'application/pdf' || ext === 'pdf') return 'pdf';
  if (SHEET_TYPES.includes(mime) || ['xlsx', 'xls', 'csv'].includes(ext)) return 'sheet';
  if (TEXT_TYPES.includes(mime) || ['txt', 'md', 'json', 'js', 'ts', 'tsx', 'jsx', 'html', 'css', 'xml', 'log'].includes(ext)) return 'text';
  return 'none';
};

export interface FileCategory {
  label: string;
  classes: string;
}

export const getFileCategory = (file: ThumbFile): FileCategory => {
  // Solid primary blue background with white text for readability.
  const blue = 'bg-primary-500 text-white';
  switch (getThumbType(file)) {
    case 'image':
      return { label: 'Image', classes: blue };
    case 'video':
      return { label: 'Video', classes: blue };
    case 'audio':
      return { label: 'Audio', classes: blue };
    case 'pdf':
      return { label: 'Document', classes: blue };
    case 'sheet':
      return { label: 'Spreadsheet', classes: blue };
    case 'text':
      return { label: 'Text', classes: blue };
    default:
      return { label: 'File', classes: blue };
  }
};

const getPublicUrl = (file: ThumbFile): string => {
  const { data } = supabase.storage.from('file-storage').getPublicUrl(file.storage_path);
  return data.publicUrl;
};

interface FileThumbProps {
  file: ThumbFile;
  size?: 'sm' | 'md';
}

const MAX_ROWS = 5;
const MAX_COLS = 6;

const SheetMiniPreview: React.FC<{ url: string; onError: () => void }> = ({ url, onError }) => {
  const [rows, setRows] = useState<any[][] | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetch(url)
      .then(res => {
        if (!res.ok) throw new Error('fetch failed');
        return res.arrayBuffer();
      })
      .then(buffer => {
        const wb = XLSX.read(buffer);
        const ws = wb.Sheets[wb.SheetNames[0]];
        const data = XLSX.utils.sheet_to_json<any[]>(ws, { header: 1 });
        if (!cancelled) setRows(data.slice(0, MAX_ROWS));
      })
      .catch(() => { if (!cancelled) setError(true); });
    return () => { cancelled = true; };
  }, [url]);

  if (error) { onError(); return null; }
  if (!rows) return null;

  const maxCols = Math.min(MAX_COLS, Math.max(...rows.map(r => (r || []).length), 1));

  return (
    <div className="w-full h-full overflow-hidden bg-white p-1 pointer-events-none select-none">
      <table className="w-full border-collapse text-[5px] leading-tight">
        <tbody>
          {rows.map((row, ri) => (
            <tr key={ri}>
              {Array.from({ length: maxCols }).map((_, ci) => (
                <td
                  key={ci}
                  className={`border border-gray-200 px-0.5 py-0 ${ri === 0 ? 'bg-gray-100 font-medium' : ''} text-gray-700 truncate max-w-[40px]`}
                >
                  {row?.[ci] !== undefined && row[ci] !== null ? String(row[ci]) : ''}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const TextMiniPreview: React.FC<{ url: string; onError: () => void }> = ({ url, onError }) => {
  const [content, setContent] = useState<string | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetch(url)
      .then(res => {
        if (!res.ok) throw new Error('fetch failed');
        return res.text();
      })
      .then(text => { if (!cancelled) setContent(text); })
      .catch(() => { if (!cancelled) setError(true); });
    return () => { cancelled = true; };
  }, [url]);

  if (error) { onError(); return null; }
  if (content === null) return null;

  return (
    <div className="w-full h-full overflow-hidden bg-gray-950 p-1.5 pointer-events-none select-none">
      <pre className="text-[5px] leading-tight text-gray-400 whitespace-pre-wrap break-words line-clamp-6 font-mono">
        {content.slice(0, 600)}
      </pre>
    </div>
  );
};

const PdfMiniPreview: React.FC<{ url: string; onError: () => void }> = ({ url, onError }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [failed, setFailed] = useState(false);
  const errorCalledRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    let timedOut = false;

    const timeout = setTimeout(() => {
      timedOut = true;
      if (!cancelled) setFailed(true);
    }, 8000);

    (async () => {
      try {
        const doc = await pdfjsLib.getDocument({ url, isEvalSupported: false }).promise;
        if (cancelled || timedOut) return;
        const page = await doc.getPage(1);
        if (cancelled || timedOut) return;
        const canvas = canvasRef.current;
        if (!canvas) return;
        // Render at a fixed small scale for the thumbnail
        const viewport = page.getViewport({ scale: 0.5 });
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        await page.render({ canvasContext: canvas.getContext('2d')!, viewport }).promise;
      } catch (err) {
        console.error('PDF thumb error:', err);
        if (!cancelled) setFailed(true);
      } finally {
        clearTimeout(timeout);
      }
    })();

    return () => { cancelled = true; clearTimeout(timeout); };
  }, [url]);

  useEffect(() => {
    if (failed && !errorCalledRef.current) {
      errorCalledRef.current = true;
      onError();
    }
  }, [failed, onError]);

  if (failed) return null;

  return (
    <div className="w-full h-full overflow-hidden bg-white flex items-center justify-center">
      <canvas ref={canvasRef} className="max-w-full max-h-full object-contain" />
    </div>
  );
};

export const VideoThumb: React.FC<{ src: string; autoPlay: boolean }> = ({ src, autoPlay }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    if (autoPlay) {
      video.play().catch(() => {});
    } else {
      video.pause();
      video.currentTime = 0.5;
    }
    setPlaying(autoPlay);
  }, [autoPlay]);

  return (
    <div
      className="relative w-full h-full bg-black"
      onMouseEnter={() => { videoRef.current?.play().catch(() => {}); setPlaying(true); }}
      onMouseLeave={() => { videoRef.current?.pause(); setPlaying(false); }}
    >
      <video
        ref={videoRef}
        src={src}
        preload="metadata"
        muted
        loop
        playsInline
        className="w-full h-full object-cover"
      />
      {!playing && <PlayIcon className="absolute inset-0 m-auto w-4 h-4 text-white/80" />}
    </div>
  );
};

export const FileThumb: React.FC<FileThumbProps> = ({ file, size = 'md' }) => {
  const [fallback, setFallback] = useState(false);
  const [hovered, setHovered] = useState(false);
  const thumbType = getThumbType(file);
  const url = getPublicUrl(file);
  const boxClass = size === 'sm' ? 'w-8 h-8' : 'w-full aspect-square';

  const renderContent = () => {
    if (fallback) return <File className="w-6 h-6 text-gray-400" />;
    switch (thumbType) {
      case 'image':
        return <img src={url} alt={file.name} className="w-full h-full object-cover" loading="lazy" />;
      case 'video':
        return (
          <VideoThumb
            src={url}
            autoPlay={size !== 'sm' && hovered}
          />
        );
      case 'pdf':
        return <PdfMiniPreview url={url} onError={() => setFallback(true)} />;
      case 'sheet':
        return (
          <div className="relative w-full h-full bg-white">
            <SheetMiniPreview url={url} onError={() => setFallback(true)} />
            <Table2 className="absolute bottom-1 right-1 w-3 h-3 text-gray-600" />
          </div>
        );
      case 'text':
        return (
          <div className="w-full h-full bg-gray-950">
            <TextMiniPreview url={url} onError={() => setFallback(true)} />
          </div>
        );
      default:
        return (
          <div className="w-full h-full flex items-center justify-center bg-gray-800/50">
            <File className="w-6 h-6 text-gray-400" />
          </div>
        );
    }
  };

  return (
    <div
      className={`${boxClass} overflow-hidden rounded-md flex-shrink-0 bg-gray-800/50`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {renderContent()}
    </div>
  );
};
